/**
 * Vintage Ribelle - Product Page
 * Premium vintage fashion brand
 * Vanilla JS, ES6+, zero dependencies
 *
 * Handles: variant selection, media gallery, image zoom,
 *          and product form submission.
 */

class ProductPage {
  constructor() {
    this.productForm = document.querySelector('[data-product-form]');
    this.variantInputs = document.querySelectorAll('[data-variant-input]');
    this.variantSelects = document.querySelectorAll('[data-variant-select]');
    this.priceDisplay = document.querySelector('[data-price-display]');
    this.comparePrice = document.querySelector('[data-compare-price]');
    this.addToCartBtn = document.querySelector('[data-add-to-cart]');
    this.quantityInput = document.querySelector('[data-quantity-input]');
    this.gallery = document.querySelector('.media-gallery');

    // Product data (set by Shopify via data attribute or JSON script tag)
    this.productData = this.parseProductData();

    // Gallery state
    this.currentMediaIndex = 0;
    this.mediaItems = [];
    this.zoomEnabled = false;

    this.init();
  }

  /* ==================================================================
     Initialization
     ================================================================== */

  init() {
    if (!this.productForm) return;

    this.parseProductData();
    this.initVariantSelection();
    this.initGallery();
    this.initZoom();
    this.bindFormSubmit();
    this.initStickyAddToCart();

    // Set initial state
    this.updateFormState();
  }

  /* ==================================================================
     Product Data
     ================================================================== */

  parseProductData() {
    // Try to get product JSON from script tag (Shopify's standard pattern)
    const productJsonEl = document.getElementById('ProductJson');
    if (productJsonEl) {
      try {
        this.productData = JSON.parse(productJsonEl.textContent);
        return this.productData;
      } catch (err) {
        console.error('Failed to parse product JSON:', err);
      }
    }

    // Fallback: read from data attribute on product form
    const productDataAttr = this.productForm
      ? this.productForm.getAttribute('data-product-data')
      : null;
    if (productDataAttr) {
      try {
        this.productData = JSON.parse(productDataAttr);
        return this.productData;
      } catch (err) {
        console.error('Failed to parse product data attribute:', err);
      }
    }

    // Fallback: build minimal product data from DOM
    this.productData = this.buildProductDataFromDom();
    return this.productData;
  }

  buildProductDataFromDom() {
    const variants = [];
    this.variantInputs.forEach((input) => {
      if (input.type === 'radio' && input.checked) {
        variants.push({
          id: parseInt(input.value, 10),
          available: input.getAttribute('data-available') !== 'false',
        });
      }
    });

    // Build from variant selects
    const select = document.querySelector('select[name="id"]');
    if (select) {
      Array.from(select.options).forEach((option) => {
        if (option.value) {
          variants.push({
            id: parseInt(option.value, 10),
            available: !option.disabled,
            price: parseInt(option.getAttribute('data-price') || '0', 10),
            compare_at_price: parseInt(option.getAttribute('data-compare-price') || '0', 10) || null,
            featured_image: option.getAttribute('data-image') || null,
          });
        }
      });
    }

    // Collect media from gallery
    const media = [];
    const galleryImages = this.gallery
      ? this.gallery.querySelectorAll('[data-media-id]')
      : document.querySelectorAll('[data-media-id]');
    galleryImages.forEach((el) => {
      media.push({
        id: el.getAttribute('data-media-id'),
        src: el.getAttribute('data-media-src') || el.src || el.getAttribute('href'),
        alt: el.getAttribute('data-media-alt') || el.alt || '',
        type: el.getAttribute('data-media-type') || 'image',
        variant_id: el.getAttribute('data-variant-id') || null,
      });
    });

    return {
      variants: variants,
      media: media,
      available: variants.some((v) => v.available !== false),
      selected_variant_id: this.getSelectedVariantId(),
    };
  }

  /* ==================================================================
     Variant Selection
     ================================================================== */

  initVariantSelection() {
    // Radio button style variant inputs
    this.variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.onVariantChange();
      });
    });

    // Select dropdown style variant inputs
    this.variantSelects.forEach((select) => {
      select.addEventListener('change', () => {
        this.onVariantChange();
      });
    });

    // Single variant or no options: just update availability
    if (this.productData && this.productData.variants && this.productData.variants.length <= 1) {
      this.updateAvailabilityState();
    }
  }

  onVariantChange() {
    const variant = this.findSelectedVariant();
    this.updatePriceDisplay(variant);
    this.updateAvailabilityState(variant);
    this.updateFeaturedImage(variant);
  }

  findSelectedVariant() {
    if (!this.productData || !this.productData.variants) return null;

    // First check: hidden select[name="id"]
    const idSelect = document.querySelector('select[name="id"]');
    if (idSelect && idSelect.value) {
      const selectedId = parseInt(idSelect.value, 10);
      return this.productData.variants.find((v) => v.id === selectedId) || null;
    }

    // Second: collect selected option values
    const selectedOptions = [];
    this.variantSelects.forEach((select) => {
      if (select.value) selectedOptions.push(select.value);
    });

    this.variantInputs.forEach((input) => {
      if (input.checked) selectedOptions.push(input.value);
    });

    // Match variant by combining option values
    // Option format in Shopify: "Option1 / Option2 / Option3"
    const optionString = selectedOptions.join(' / ');

    return (
      this.productData.variants.find((v) => {
        if (v.option1 === selectedOptions[0] && v.option2 === selectedOptions[1] && v.option3 === selectedOptions[2]) {
          return true;
        }
        // Also try matching by title or option string
        const vOptions = [v.option1, v.option2, v.option3].filter(Boolean).join(' / ');
        return vOptions === optionString;
      }) || this.productData.variants[0] || null
    );
  }

  updatePriceDisplay(variant) {
    if (!this.priceDisplay) return;

    if (variant && variant.price) {
      this.priceDisplay.textContent = this.formatMoney(variant.price);
      this.priceDisplay.classList.remove('price--unavailable');
    } else if (this.productData && this.productData.price) {
      this.priceDisplay.textContent = this.formatMoney(this.productData.price);
    }

    // Compare-at (original) price
    if (this.comparePrice) {
      const compareAt = variant
        ? variant.compare_at_price
        : this.productData
          ? this.productData.compare_at_price
          : null;

      if (compareAt && compareAt > (variant ? variant.price : this.productData.price)) {
        this.comparePrice.textContent = this.formatMoney(compareAt);
        this.comparePrice.style.display = '';
      } else {
        this.comparePrice.style.display = 'none';
      }
    }
  }

  updateAvailabilityState(variant) {
    if (!this.addToCartBtn) return;

    const isAvailable = variant
      ? variant.available !== false
      : this.productData
        ? this.productData.available !== false
        : true;

    if (isAvailable) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.classList.remove('btn--sold-out');
      this.addToCartBtn.textContent =
        this.addToCartBtn.getAttribute('data-add-to-cart-text') || 'Aggiungi al carrello';
    } else {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.add('btn--sold-out');
      this.addToCartBtn.textContent =
        this.addToCartBtn.getAttribute('data-sold-out-text') || 'Esaurito';
    }

    // Update other availability indicators
    const availabilityEl = document.querySelector('[data-availability]');
    if (availabilityEl) {
      availabilityEl.textContent = isAvailable ? 'Disponibile' : 'Esaurito';
      availabilityEl.classList.toggle('is-available', isAvailable);
      availabilityEl.classList.toggle('is-unavailable', !isAvailable);
    }
  }

  updateFeaturedImage(variant) {
    if (!variant || !variant.featured_image) return;

    // Find and activate the media item associated with this variant's image
    const mediaEl = document.querySelector(`[data-variant-id="${variant.id}"]`);
    if (mediaEl && this.gallery) {
      const thumbnail = this.gallery.querySelector(`[data-media-id="${mediaEl.getAttribute('data-media-id')}"]`);
      if (thumbnail) {
        thumbnail.click();
      }
      return;
    }

    // Fallback: update main image src directly
    const mainImage = this.gallery
      ? this.gallery.querySelector('.media-gallery__main img')
      : document.querySelector('.media-gallery__main img');

    if (mainImage && variant.featured_image && variant.featured_image.src) {
      mainImage.src = variant.featured_image.src;
      mainImage.alt = variant.featured_image.alt || '';
    }
  }

  updateFormState() {
    const variant = this.findSelectedVariant();
    if (variant) {
      this.updatePriceDisplay(variant);
      this.updateAvailabilityState(variant);
      this.updateFeaturedImage(variant);

      // Update hidden variant ID input
      const variantIdInput = document.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }
    }

    // Update add to cart button variant-id attribute
    if (this.addToCartBtn && variant) {
      this.addToCartBtn.setAttribute('data-variant-id', variant.id);
    }
  }

  getSelectedVariantId() {
    const input = document.querySelector('input[name="id"]');
    if (input && input.value) return parseInt(input.value, 10);

    const select = document.querySelector('select[name="id"]');
    if (select && select.value) return parseInt(select.value, 10);

    return null;
  }

  /* ==================================================================
     Media Gallery
     ================================================================== */

  initGallery() {
    if (!this.gallery) return;

    this.mediaItems = Array.from(
      this.gallery.querySelectorAll('[data-media-item]')
    );

    if (this.mediaItems.length === 0) return;

    // Thumbnail clicks
    const thumbnails = this.gallery.querySelectorAll('.media-gallery__thumb');
    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToMedia(index);
      });
    });

    // Navigation buttons
    const prevBtn = this.gallery.querySelector('[data-gallery-prev]');
    const nextBtn = this.gallery.querySelector('[data-gallery-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prevMedia();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.nextMedia();
      });
    }

    // Keyboard navigation
    this.gallery.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prevMedia();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.nextMedia();
      }
    });

    // Touch swipe support for mobile
    this.initSwipeNavigation();

    // Initialize first media
    this.goToMedia(0);
  }

  goToMedia(index) {
    if (this.mediaItems.length === 0) return;

    // Loop around
    if (index < 0) index = this.mediaItems.length - 1;
    if (index >= this.mediaItems.length) index = 0;

    this.currentMediaIndex = index;

    // Update main display area
    const mainArea = this.gallery.querySelector('.media-gallery__main');
    if (mainArea) {
      const currentItem = this.mediaItems[index];
      const mediaType = currentItem.getAttribute('data-media-type') || 'image';
      const mediaSrc = currentItem.getAttribute('data-media-src') || currentItem.getAttribute('href');
      const mediaAlt = currentItem.getAttribute('data-media-alt') || '';

      if (mediaType === 'video' || mediaType === 'external_video') {
        this.renderVideoInMain(mainArea, currentItem);
      } else {
        this.renderImageInMain(mainArea, mediaSrc, mediaAlt, index);
      }
    }

    // Update thumbnail active state
    const thumbnails = this.gallery.querySelectorAll('.media-gallery__thumb');
    thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
    });

    // Update counter display
    const counter = this.gallery.querySelector('[data-gallery-counter]');
    if (counter) {
      counter.textContent = `${index + 1} / ${this.mediaItems.length}`;
    }

    // Dispatch event
    this.gallery.dispatchEvent(
      new CustomEvent('gallery:changed', {
        detail: { index, mediaItem: this.mediaItems[index] },
      })
    );
  }

  renderImageInMain(container, src, alt, index) {
    let img = container.querySelector('img:not(.media-gallery__thumb img)');
    if (!img) {
      // If current content is a video, clear it
      container.innerHTML = '';
      img = document.createElement('img');
      img.className = 'media-gallery__image';
      container.appendChild(img);
    }

    // Remove any video elements
    const video = container.querySelector('video, iframe');
    if (video) video.remove();

    img.src = src;
    img.alt = alt || '';
    img.setAttribute('data-index', index);

    // Enable zoom on new image
    if (this.zoomEnabled) {
      img.style.cursor = 'zoom-in';
    }
  }

  renderVideoInMain(container, mediaItem) {
    const videoType = mediaItem.getAttribute('data-media-type');
    const videoSrc = mediaItem.getAttribute('data-media-src');
    const videoId = mediaItem.getAttribute('data-video-id');

    container.innerHTML = '';

    if (videoType === 'external_video' && videoId) {
      // YouTube/Vimeo embed
      const iframe = document.createElement('iframe');
      iframe.className = 'media-gallery__video-iframe';
      iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`;
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('allow', 'autoplay; encrypted-media');
      container.appendChild(iframe);
    } else if (videoSrc) {
      // HTML5 video
      const video = document.createElement('video');
      video.className = 'media-gallery__video';
      video.src = videoSrc;
      video.controls = true;
      video.playsInline = true;
      container.appendChild(video);
    }
  }

  nextMedia() {
    this.goToMedia(this.currentMediaIndex + 1);
  }

  prevMedia() {
    this.goToMedia(this.currentMediaIndex - 1);
  }

  initSwipeNavigation() {
    if (!this.gallery) return;

    const mainArea = this.gallery.querySelector('.media-gallery__main');
    if (!mainArea) return;

    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    mainArea.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    mainArea.addEventListener(
      'touchend',
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const delta = touchStartX - touchEndX;

        if (Math.abs(delta) > minSwipeDistance) {
          if (delta > 0) {
            this.nextMedia();
          } else {
            this.prevMedia();
          }
        }
      },
      { passive: true }
    );
  }

  /* ==================================================================
     Image Zoom
     ================================================================== */

  initZoom() {
    if (!this.gallery) return;

    // Only enable zoom on devices likely to have a mouse
    const hasFinePointer = window.matchMedia('(any-pointer: fine)').matches;
    if (!hasFinePointer) return;

    const mainArea = this.gallery.querySelector('.media-gallery__main');
    if (!mainArea) return;

    this.zoomEnabled = true;

    mainArea.addEventListener('mousemove', (e) => this.handleZoomMove(e, mainArea));
    mainArea.addEventListener('mouseenter', () => this.handleZoomEnter(mainArea));
    mainArea.addEventListener('mouseleave', () => this.handleZoomLeave(mainArea));
  }

  handleZoomEnter(container) {
    const img = container.querySelector('img');
    if (!img) return;
    img.style.transition = 'transform 0.1s ease-out';
  }

  handleZoomMove(e, container) {
    const img = container.querySelector('img');
    if (!img) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left; // X position within the container
    const y = e.clientY - rect.top;  // Y position within the container

    // Calculate percentage position
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;

    // Set transform origin to mouse position and scale
    img.style.transformOrigin = `${xPercent}% ${yPercent}%`;
    img.style.transform = 'scale(1.8)';
  }

  handleZoomLeave(container) {
    const img = container.querySelector('img');
    if (!img) return;
    img.style.transform = 'scale(1)';
  }

  /* ==================================================================
     Form Submit
     ================================================================== */

  bindFormSubmit() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', (e) => {
      e.preventDefault();

      const variant = this.findSelectedVariant();
      if (!variant) {
        this.showFormError('Seleziona le opzioni del prodotto prima di aggiungere al carrello.');
        return;
      }

      if (variant.available === false) {
        this.showFormError('Questo prodotto non &egrave; al momento disponibile.');
        return;
      }

      // Update the hidden variant input
      const idInput = this.productForm.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variant.id;
      } else {
        // Create hidden input if not present
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = 'id';
        hiddenInput.value = variant.id;
        this.productForm.appendChild(hiddenInput);
      }

      // Update add-to-cart button variant-id
      if (this.addToCartBtn) {
        this.addToCartBtn.setAttribute('data-variant-id', variant.id);
      }

      // Submit via CartManager if available, otherwise direct form POST
      if (window.cartManager && typeof window.cartManager.addItemToCart === 'function') {
        const quantity = this.quantityInput
          ? parseInt(this.quantityInput.value, 10) || 1
          : 1;

        // Collect line item properties if any
        const properties = {};
        const propertyInputs = this.productForm.querySelectorAll('[data-cart-property]');
        propertyInputs.forEach((input) => {
          if (input.name && input.value) {
            properties[input.name] = input.value;
          }
        });

        window.cartManager.addItemToCart(variant.id, quantity, properties, this.addToCartBtn);
      } else {
        // Fallback: direct form post
        this.productForm.submit();
      }
    });
  }

  showFormError(message) {
    // Remove existing form errors
    const existingError = this.productForm.querySelector('.form-error');
    if (existingError) existingError.remove();

    const errorEl = document.createElement('div');
    errorEl.className = 'form-error';
    errorEl.innerHTML = message;
    errorEl.style.cssText = `
      color: #e74c3c;
      font-size: 0.85rem;
      margin-top: 1rem;
      padding: 0.75rem;
      background: rgba(231, 76, 60, 0.1);
      border: 1px solid rgba(231, 76, 60, 0.3);
      border-radius: 4px;
    `;

    const submitBtn = this.productForm.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.insertAdjacentElement('afterend', errorEl);
    } else {
      this.productForm.appendChild(errorEl);
    }

    // Scroll to error
    errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Auto-remove after 6 seconds
    setTimeout(() => {
      if (errorEl.parentElement) errorEl.remove();
    }, 6000);
  }

  /* ==================================================================
     Sticky Add to Cart (Mobile)
     ================================================================== */

  initStickyAddToCart() {
    const stickyBar = document.querySelector('[data-sticky-atc]');
    if (!stickyBar) return;

    const productSection = document.querySelector('[data-product-section]');
    if (!productSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('is-visible');
          } else {
            stickyBar.classList.add('is-visible');
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(productSection);

    // Sync sticky bar button with main product form
    const stickyBtn = stickyBar.querySelector('[data-add-to-cart]');
    if (stickyBtn && this.addToCartBtn) {
      stickyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        // Sync variant ID
        const variantId = this.addToCartBtn.getAttribute('data-variant-id');
        if (variantId) {
          stickyBtn.setAttribute('data-variant-id', variantId);
        }
        // Trigger click on main button
        this.addToCartBtn.click();
      });
    }
  }

  /* ==================================================================
     Utility Functions
     ================================================================== */

  /**
   * Format a price in cents to a display string.
   * Delegates to CartManager if available, otherwise simple EUR format.
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    if (window.cartManager && typeof window.cartManager.formatMoney === 'function') {
      return window.cartManager.formatMoney(cents);
    }

    const amount = typeof cents === 'number' ? cents : parseInt(cents, 10) || 0;
    const euros = (amount / 100).toFixed(2);
    const parts = euros.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `€${parts.join(',')}`;
  }
}

/* ==================================================================
   Bootstrap
   ================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  window.productPage = new ProductPage();
});
